import { Routes } from '@share/constants';

export type StripeExpressVertical = 'hotel' | 'condo' | 'weeks' | 'cars';

/** sessionStorage key prefix — full pre-booking query (without `?`) for Stripe return_url shrink. */
export const STRIPE_RETURN_QUERY_STORAGE_PREFIX = 'stripeExpressReturn:';

/**
 * Query param on return from Stripe: id for {@link STRIPE_RETURN_QUERY_STORAGE_PREFIX} payload.
 * Keeps `return_url` under Stripe's 2048-char limit (see payment_intents/confirm).
 */
export const STRIPE_RETURN_SER_PARAM = 'ser';

/** Stripe rejects `return_url` longer than this (payment_intents/confirm). */
const STRIPE_RETURN_URL_CHAR_LIMIT = 2048;

/**
 * Merge `overrides` into a raw query string WITHOUT round-tripping the existing pairs through
 * `URLSearchParams`. The app stores base64 payloads (carsDates, sessionKey, …) raw in the URL and
 * reads them back with a plain slice + `Base64.decode` (no percent-decoding) — see UrlUtils.getValues.
 * `URLSearchParams.toString()` would percent-encode `+` `/` `=`, corrupting those payloads on return.
 * Existing pairs are preserved verbatim; only the overridden keys are (re)written, URL-encoded.
 */
function upsertRawQueryParams(rawQuery: string, overrides: Record<string, string>): string {
  const overrideKeys = new Set(Object.keys(overrides));
  const kept = (rawQuery ? rawQuery.split('&') : []).filter((pair) => {
    if (!pair) {
      return false;
    }
    const eq = pair.indexOf('=');
    const key = eq === -1 ? pair : pair.slice(0, eq);
    return !overrideKeys.has(key);
  });
  for (const [key, value] of Object.entries(overrides)) {
    kept.push(`${key}=${encodeURIComponent(value)}`);
  }
  return kept.join('&');
}

/** Path + query under the account segment, e.g. `/book/1/pkg?sessionKey=…` */
export function getStripeExpressReturnToRelativePath(accountUsername: string): string | undefined {
  if (typeof window === 'undefined' || !accountUsername) {
    return undefined;
  }
  const prefix = `/${accountUsername}`;
  const { pathname, search } = window.location;
  if (!pathname.startsWith(prefix)) {
    return undefined;
  }
  return `${pathname.slice(prefix.length)}${search}`;
}

function bookRelativePathToStripeBookPath(pathOnly: string): string {
  if (
    pathOnly.includes(`${Routes.StripeBookHotel}/`) ||
    pathOnly.includes(`${Routes.StripeBookCondo}/`) ||
    pathOnly.includes(`${Routes.StripeBookWeek}/`) ||
    pathOnly.includes(`${Routes.StripeBookCar}/`)
  ) {
    return pathOnly;
  }
  let p = pathOnly;
  if (p.includes('/weeks/book/')) {
    p = p.replace('/weeks/book/', `${Routes.StripeBookWeek}/`);
  } else if (p.includes(`${Routes.UnitBook}/`) || p.startsWith(`${Routes.UnitBook}/`)) {
    p = p.replace(Routes.UnitBook, Routes.StripeBookCondo);
  } else if (p.includes(`${Routes.CarsDetails}/`)) {
    p = p.replace(Routes.CarsDetails, Routes.StripeBookCar);
  } else if (p.includes(`${Routes.Book}/`)) {
    p = p.replace(Routes.Book, Routes.StripeBookHotel);
  }
  return p;
}

/** Query params added by the Stripe resume flow (and by Stripe itself) — dropped when returning to checkout. */
const STRIPE_RESUME_PARAM_KEYS = [
  'bookingId',
  'booking_id',
  'BookingId',
  'sessionId',
  'session_id',
  'paymentMethod',
  'payment_method',
  'redirect_status',
  'payment_intent',
  'payment_intent_client_secret',
  'setup_intent',
  'setup_intent_client_secret',
  STRIPE_RETURN_SER_PARAM,
] as const;

/** Remove keys from a raw query string without re-encoding the remaining (possibly base64) values. */
function removeRawQueryParams(rawQuery: string, keys: readonly string[]): string {
  const remove = new Set(keys);
  return (rawQuery ? rawQuery.split('&') : [])
    .filter(Boolean)
    .filter((pair) => {
      const eq = pair.indexOf('=');
      const key = eq === -1 ? pair : pair.slice(0, eq);
      return !remove.has(key);
    })
    .join('&');
}

/** Reverse of {@link bookRelativePathToStripeBookPath}: map a `/stripe/{vertical}` book path back to its checkout route. */
function stripeBookPathToCheckoutPath(pathname: string): string {
  if (pathname.includes(`${Routes.StripeBookHotel}/`)) {
    return pathname.replace(Routes.StripeBookHotel, Routes.Book);
  }
  if (pathname.includes(`${Routes.StripeBookCondo}/`)) {
    return pathname.replace(Routes.StripeBookCondo, Routes.UnitBook);
  }
  if (pathname.includes(`${Routes.StripeBookWeek}/`)) {
    return pathname.replace(Routes.StripeBookWeek, Routes.WeeksBook);
  }
  if (pathname.includes(`${Routes.StripeBookCar}/`)) {
    return pathname.replace(Routes.StripeBookCar, Routes.CarsDetails);
  }
  return pathname;
}

/**
 * "Back to checkout" href used when a Stripe express resume fails: reverses the `/stripe/{vertical}`
 * book path to the original checkout route and drops the resume-only params, while preserving the
 * original (raw base64) checkout query so the page can rebuild itself.
 */
export function getStripeCheckoutBackHref(pathname: string, search: string): string {
  const checkoutPath = stripeBookPathToCheckoutPath(pathname);
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const cleaned = removeRawQueryParams(raw, STRIPE_RESUME_PARAM_KEYS);
  return cleaned ? `${checkoutPath}?${cleaned}` : checkoutPath;
}

/**
 * "Go to search" href used when there's no Stripe booking to resume from this URL (e.g., user
 * refreshes after a successful booking — the resume params were stripped). Maps the
 * `/stripe/{vertical}/…` path to that vertical's search route, preserving the `/{account}` prefix.
 */
export function getStripeSearchHrefFromPath(pathname: string): string {
  const verticalMap: ReadonlyArray<readonly [string, string]> = [
    [`${Routes.StripeBookHotel}/`, Routes.Search],
    [`${Routes.StripeBookCondo}/`, Routes.CondoSearch],
    [`${Routes.StripeBookWeek}/`, Routes.WeeksSearch],
    [`${Routes.StripeBookCar}/`, Routes.CarsSearch],
  ];
  for (const [stripeBookSegment, searchRoute] of verticalMap) {
    const idx = pathname.indexOf(stripeBookSegment);
    if (idx !== -1) {
      return `${pathname.slice(0, idx)}${searchRoute}`;
    }
  }
  return pathname;
}

function newStripeReturnStorageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * After redirect from Stripe, expand a short URL: restores full `?...` from sessionStorage
 * (see {@link buildStripeExpressReturnUrl}) and `history.replace`s so UrlUtils / book flow see all params.
 * No-op if `ser` is absent. Safe to call multiple times.
 */
export function hydrateStripeExpressReturnSearch(history: {
  location: { pathname: string; search: string };
  replace: (loc: { pathname: string; search: string }) => void;
}): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const { pathname, search } = history.location;
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(raw);
  const ser = params.get(STRIPE_RETURN_SER_PARAM);
  if (!ser) {
    return false;
  }
  let stored = '';
  try {
    stored = sessionStorage.getItem(STRIPE_RETURN_QUERY_STORAGE_PREFIX + ser) ?? '';
  } catch {
    return false;
  }
  const overlayKeys = [
    'bookingId',
    'booking_id',
    'BookingId',
    'sessionId',
    'session_id',
    'paymentMethod',
    'payment_method',
    'redirect_status',
  ] as const;
  const overlay: Record<string, string> = {};
  for (const k of overlayKeys) {
    const v = params.get(k);
    if (v != null) {
      overlay[k] = v;
    }
  }
  try {
    sessionStorage.removeItem(STRIPE_RETURN_QUERY_STORAGE_PREFIX + ser);
  } catch {
    /* ignore */
  }
  // `stored` holds the original raw query (base64 payloads); merge without URLSearchParams re-encoding.
  const next = upsertRawQueryParams(stored, overlay);
  history.replace({ pathname, search: next ? `?${next}` : '' });
  return true;
}

export function buildStripeExpressReturnUrl(options: {
  accountUsername?: string | null;
  vertical: StripeExpressVertical;
  bookingGuid: string;
  sessionId: string;
  paymentMethod: string;
  fallbackAbsoluteUrl: string;
}): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const acc = options.accountUsername;
  if (!acc || !origin) {
    return options.fallbackAbsoluteUrl;
  }

  const rel = getStripeExpressReturnToRelativePath(acc);
  if (!rel) {
    return options.fallbackAbsoluteUrl;
  }

  const [pathOnly, existingQuery = ''] = rel.split('?');
  const stripeBookPath = bookRelativePathToStripeBookPath(pathOnly);

  /** Full current query (base64 payloads preserved verbatim) + Stripe resume params. */
  const legacyQs = upsertRawQueryParams(existingQuery ?? '', {
    bookingId: options.bookingGuid,
    sessionId: options.sessionId,
    paymentMethod: options.paymentMethod,
  });
  const legacyReturnUrl = `${origin}/${acc}${stripeBookPath}${legacyQs ? `?${legacyQs}` : ''}`;

  if (legacyReturnUrl.length <= STRIPE_RETURN_URL_CHAR_LIMIT) {
    return legacyReturnUrl;
  }

  /** Only when the legacy URL would exceed Stripe’s limit: short URL + sessionStorage restore on return. */
  const ser = newStripeReturnStorageId();
  try {
    sessionStorage.setItem(STRIPE_RETURN_QUERY_STORAGE_PREFIX + ser, existingQuery);
  } catch {
    /* quota / private mode — still return short URL so confirm doesn’t 400 */
  }

  const short = new URLSearchParams();
  short.set('bookingId', options.bookingGuid);
  short.set('sessionId', options.sessionId);
  short.set('paymentMethod', options.paymentMethod);
  short.set(STRIPE_RETURN_SER_PARAM, ser);

  const shrunk = `${origin}/${acc}${stripeBookPath}?${short.toString()}`;
  return shrunk.length <= STRIPE_RETURN_URL_CHAR_LIMIT
    ? shrunk
    : options.fallbackAbsoluteUrl;
}
